import type { ApiClient } from '@hetivo/hetivo-lib-front-utils';
import { toPaginatedResponse } from '@hetivo/hetivo-lib-front-utils';
import type {
  CommentIssueBody,
  HetivoApiResponse,
  HetivoPaginatedResponse,
  IssueDetail,
  IssueListItem,
  IssueStats,
  IssueTimelineEntryView,
  PaginatedResponse,
  ReportIssueBody,
  ResolveIssueBody,
  SearchIssuesCriteria,
} from '@hetivo/hetivo-lib-types';

// ─── Params ───────────────────────────────────────────────────────────────────

/** Search criteria plus pagination. The list endpoint takes `page`/`limit` on the query string. */
export interface SearchIssuesParams extends SearchIssuesCriteria {
  /** 1-based page number. */
  page?: number;
  /** Rows per page (server caps at 100). */
  limit?: number;
}

// ─── API factory ──────────────────────────────────────────────────────────────

export function createIssueApi(client: ApiClient): {
  comment: (id: string, body: CommentIssueBody) => Promise<IssueTimelineEntryView>;
  getById: (id: string) => Promise<IssueDetail>;
  reopen: (id: string) => Promise<IssueDetail>;
  report: (body: ReportIssueBody) => Promise<IssueDetail>;
  resolve: (id: string, body: ResolveIssueBody) => Promise<IssueDetail>;
  search: (params: SearchIssuesParams) => Promise<PaginatedResponse<IssueListItem>>;
  start: (id: string) => Promise<IssueDetail>;
  stats: (criteria: SearchIssuesCriteria) => Promise<IssueStats>;
} {
  return {
    comment: async (id: string, body: CommentIssueBody): Promise<IssueTimelineEntryView> => {
      const response = await client.execute<HetivoApiResponse<IssueTimelineEntryView>>(() =>
        client.instance.post(`/issues/${id}/comment`, body),
      );
      if (!response.data) {
        throw new Error('comment: unexpected empty response');
      }
      return response.data;
    },

    getById: async (id: string): Promise<IssueDetail> => {
      const response = await client.execute<HetivoApiResponse<IssueDetail>>(() => client.instance.get(`/issues/${id}`));
      if (!response.data) {
        throw new Error('getById: unexpected empty response');
      }
      return response.data;
    },

    reopen: async (id: string): Promise<IssueDetail> => {
      const response = await client.execute<HetivoApiResponse<IssueDetail>>(() =>
        client.instance.post(`/issues/${id}/reopen`),
      );
      if (!response.data) {
        throw new Error('reopen: unexpected empty response');
      }
      return response.data;
    },

    report: async (body: ReportIssueBody): Promise<IssueDetail> => {
      const response = await client.execute<HetivoApiResponse<IssueDetail>>(() =>
        client.instance.post('/issues', body),
      );
      if (!response.data) {
        throw new Error('report: unexpected empty response');
      }
      return response.data;
    },

    resolve: async (id: string, body: ResolveIssueBody): Promise<IssueDetail> => {
      const response = await client.execute<HetivoApiResponse<IssueDetail>>(() =>
        client.instance.post(`/issues/${id}/resolve`, body),
      );
      if (!response.data) {
        throw new Error('resolve: unexpected empty response');
      }
      return response.data;
    },

    search: async (params: SearchIssuesParams): Promise<PaginatedResponse<IssueListItem>> => {
      const { limit, page, ...criteria } = params;
      const response = await client.execute<HetivoApiResponse<HetivoPaginatedResponse<IssueListItem>>>(() =>
        client.instance.post('/issues/search', toCriteriaBody(criteria), { params: { limit, page } }),
      );
      if (!response.data) {
        throw new Error('search: unexpected empty response');
      }
      return toPaginatedResponse(response.data);
    },

    start: async (id: string): Promise<IssueDetail> => {
      const response = await client.execute<HetivoApiResponse<IssueDetail>>(() =>
        client.instance.post(`/issues/${id}/start`),
      );
      if (!response.data) {
        throw new Error('start: unexpected empty response');
      }
      return response.data;
    },

    stats: async (criteria: SearchIssuesCriteria): Promise<IssueStats> => {
      const response = await client.execute<HetivoApiResponse<IssueStats>>(() =>
        client.instance.post('/issues/stats', toCriteriaBody(criteria)),
      );
      if (!response.data) {
        throw new Error('stats: unexpected empty response');
      }
      return response.data;
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Drops empty / undefined criteria so the request body stays clean (no empty strings on the wire). */
function toCriteriaBody(criteria: SearchIssuesCriteria): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const trimmedSearch = criteria.search?.trim();
  if (trimmedSearch) {
    body.search = trimmedSearch;
  }
  if (criteria.status !== undefined) {
    body.status = criteria.status;
  }
  if (criteria.type !== undefined) {
    body.type = criteria.type;
  }
  if (criteria.territoryId !== undefined) {
    body.territoryId = criteria.territoryId;
  }
  if (criteria.locationId !== undefined) {
    body.locationId = criteria.locationId;
  }
  if (criteria.vehicleId !== undefined) {
    body.vehicleId = criteria.vehicleId;
  }
  return body;
}
